import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { formatDateTime } from "../lib/format";
import { LogoQuilla } from "./Logo";
import { Bell, House, Wrench, ShoppingCart, ChartLineUp, SignOut, Camera, Warning, X } from "@phosphor-icons/react";

const ROLE_LABEL = {
  housekeeping: "Housekeeping",
  mantenimiento: "Mantenimiento",
  compras: "Compras",
  gerencia: "Gerencia",
  quilla_admin: "Consola QUILLA",
};

// Web Audio API — sirena distintiva para alertas críticas
let audioCtx = null;
export function playEmergencySound() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const now = audioCtx.currentTime;
    // Dos ondas alternando 750Hz ↔ 500Hz — patrón de sirena de emergencia
    for (let i = 0; i < 6; i++) {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(i % 2 ? 500 : 750, now + i * 0.35);
      gain.gain.setValueAtTime(0.15, now + i * 0.35);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.35 + 0.34);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + i * 0.35);
      osc.stop(now + i * 0.35 + 0.34);
    }
  } catch (e) { /* silent */ }
}

export default function AppShell({ children, title }) {
  const { session, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [notes, setNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [weather, setWeather] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const isProtected = session?.hotel?.license_status === "PROTEGIDA";

  const loadNotes = () => {
    if (session?.hotel) api.get("/notifications").then(r => setNotes(r.data)).catch(()=>{});
  };
  useEffect(() => {
    loadNotes();
    const t = setInterval(loadNotes, 15000);
    return () => clearInterval(t);
  }, [session]);

  useEffect(() => {
    if (!session?.hotel) return;
    const load = () => {
      api.get("/weather").then(r => setWeather(r.data)).catch(()=>{});
      api.get("/weather/protocol").then(r => setProtocol(r.data)).catch(()=>{});
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    const onChange = () => load();
    window.addEventListener("quilla:protocol-changed", onChange);
    return () => { clearInterval(t); window.removeEventListener("quilla:protocol-changed", onChange); };
  }, [session]);

  // Emergencia solo cuando protocolo está activo o weather.alert=huracan real
  const protocolActive = protocol?.active === true;
  const realHurricane = weather?.alert === "huracan";
  useEffect(() => {
    if ((protocolActive || realHurricane) && !bannerDismissed) {
      const already = sessionStorage.getItem("quilla-siren-played");
      if (!already) { playEmergencySound(); sessionStorage.setItem("quilla-siren-played", "1"); }
    }
    if (!protocolActive && !realHurricane) {
      sessionStorage.removeItem("quilla-siren-played");
      setBannerDismissed(false);
    }
  }, [protocolActive, realHurricane]);

  const unread = notes.filter(n => !n.read).length;
  const critical = notes.filter(n => n.urgency === "critica" && !n.read);
  const hurricaneAlert = weather?.alert === "huracan" || weather?.alert === "tormenta_tropical";
  const showEmergency = (protocolActive || realHurricane) && !bannerDismissed;
  const isGerencia = session?.user?.role === "gerencia";

  const markRead = async (nid) => { await api.post(`/notifications/${nid}/read`); loadNotes(); };
  const openNotif = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setShowNotes(false);
    const rid = n.related?.report_id;
    if (rid) nav(`/report/${rid}`);
    else loadNotes();
  };
  const doLogout = () => { logout(); nav("/login"); };

  const roleNav = () => {
    if (!session?.user) return [];
    switch (session.user.role) {
      case "housekeeping":
        return [{to: "/housekeeping", label: "Mis reportes", icon: House},
                {to: "/housekeeping/new", label: "Reportar", icon: Camera}];
      case "mantenimiento":
        return [{to: "/mantenimiento", label: "Cola", icon: Wrench},
                {to: "/mantenimiento/new", label: "Reportar", icon: Camera}];
      case "compras":
        return [{to: "/compras", label: "Requisiciones", icon: ShoppingCart}];
      case "gerencia":
        return [{to: "/gerencia", label: "Panel", icon: ChartLineUp}];
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen pb-24 md:pb-6">
      {isProtected && (
        <div className="q-protected text-center py-2 text-sm q-safe-top" data-testid="protected-banner">
          <span className="q-label">Licencia PROTEGIDA</span> · modo solo lectura · contactar soporte QUILLA
        </div>
      )}
      {showEmergency && (
        <div className={`bg-red-600 text-white px-4 py-3 flex items-center gap-3 sticky z-40 ${isProtected ? "" : "q-safe-top top-0"}`} style={isProtected ? undefined : { top: "env(safe-area-inset-top, 0px)" }} data-testid="emergency-banner">
          <Warning size={22} weight="fill" className="q-pulse"/>
          <div className="flex-1">
            <div className="font-bold text-sm">
              {protocolActive ? "⚠ PROTOCOLO DE HURACÁN ACTIVO" : `⚠ ALERTA · ${weather.alert.replace("_", " ").toUpperCase()}`}
            </div>
            <div className="text-xs opacity-90">
              {protocolActive
                ? `Activado por ${protocol.activated_by || "gerencia"}.`
                : `Vientos hasta ${Math.round(weather.max_gust_kmh || 0)} km/h.`}
            </div>
          </div>
          {isGerencia && (
            <button onClick={() => nav("/gerencia?tab=clima")} className="bg-white text-red-700 px-3 py-1.5 rounded font-semibold text-xs" data-testid="banner-goto-clima">Ver protocolo</button>
          )}
          <button onClick={() => setBannerDismissed(true)} data-testid="banner-dismiss"
                  className="text-white/80 hover:text-white ml-2" title="Minimizar">
            <X size={18}/>
          </button>
        </div>
      )}

      <header className="sticky z-30 bg-white/85 backdrop-blur-xl border-b border-[color:var(--q-border)] q-safe-top"
              style={{ top: (isProtected || showEmergency) ? undefined : "env(safe-area-inset-top, 0px)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <LogoQuilla variant="color" size={30} />
            <div className="hidden md:block text-xs text-[color:var(--q-muted)] border-l border-[color:var(--q-border)] pl-3">
              {session?.hotel?.name || "Consola"}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:block text-right mr-2">
              <div className="text-sm font-semibold">{session?.user?.name}</div>
              <div className="q-label text-[color:var(--q-muted)]">{ROLE_LABEL[session?.user?.role]}</div>
            </div>
            <button data-testid="notifications-bell-btn" onClick={() => setShowNotes(!showNotes)} className="relative q-btn-ghost !p-2">
              <Bell size={20}/>
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 bg-[color:var(--q-gold)] text-black text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {unread}
                </span>
              )}
            </button>
            <button data-testid="logout-btn" onClick={doLogout} className="q-btn-ghost !p-2" title="Salir"><SignOut size={20}/></button>
          </div>
        </div>
      </header>

      {showNotes && (
        <div className="fixed inset-0 z-40" onClick={() => setShowNotes(false)}>
          <div className="absolute right-4 top-20 w-[92vw] max-w-md q-card p-3 max-h-[70vh] overflow-y-auto" data-testid="notifications-drawer" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 px-2">
              <div className="q-label">Bandeja de avisos</div>
              <span className="text-xs text-[color:var(--q-muted)]">{unread} sin leer</span>
            </div>
            {notes.length === 0 && <div className="text-sm text-[color:var(--q-muted)] p-4 text-center">Sin avisos.</div>}
            {notes.map(n => (
              <div key={n.id} data-testid={`notif-${n.id}`} onClick={() => openNotif(n)}
                   className={`p-3 rounded-lg mb-2 cursor-pointer border ${n.urgency === "critica" ? "border-red-500 bg-red-50" : n.read ? "border-[color:var(--q-border)] bg-white" : "border-[color:var(--q-gold)]/30 bg-[color:var(--q-gold)]/5"}`}>
                <div className="text-sm font-semibold">{n.title}</div>
                <div className="text-xs text-[color:var(--q-muted)] mt-0.5">{n.body}</div>
                <div className="text-[10px] mt-1 text-[color:var(--q-muted)] font-mono">{formatDateTime(n.ts)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {title && <h1 className="text-3xl md:text-4xl mb-6">{title}</h1>}
        {children}
      </main>

      <nav className="q-bottomnav md:hidden">
        {roleNav().map(({to, label, icon: Ic}) => {
          const active = loc.pathname === to || loc.pathname.startsWith(to + "/");
          return (
            <Link key={to} to={to} data-testid={`nav-${to.replace(/\//g, "")}`}
                  className={`flex-1 flex flex-col items-center gap-1 py-1 ${active ? "text-[color:var(--q-navy)]" : "text-[color:var(--q-muted)]"}`}>
              <Ic size={22} weight={active ? "fill" : "regular"} />
              <span className="text-[10px] font-semibold">{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
